import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import SensitiveActionHelp from '@/components/help/SensitiveActionHelp';
import sensitiveActions from '@/help/sensitiveActions';
import { translateMarketingError, slugify } from '@/help/marketingSimpleCopy';
import { Megaphone, Users, FileText, Calendar, Shield, Zap, ChevronRight, ChevronLeft, Check, Upload, AlertTriangle, Loader2 } from 'lucide-react';

const STEPS = [
  { key: 'what', label: '¿Qué?', icon: Megaphone },
  { key: 'who', label: '¿A quién?', icon: Users },
  { key: 'message', label: 'Mensaje', icon: FileText },
  { key: 'when', label: '¿Cuándo?', icon: Calendar },
  { key: 'security', label: 'Seguridad', icon: Shield },
  { key: 'test', label: 'Prueba', icon: Zap },
];

const OBJECTIVES = [
  { value: 'promotional', label: 'Promocionar servicio' },
  { value: 'follow_up', label: 'Dar seguimiento' },
  { value: 'reminder', label: 'Recordar fecha o cita' },
  { value: 'informational', label: 'Informar novedades' },
];

const CHANNELS = [
  { value: 'email_future', label: 'Email masivo (mailing)' },
  { value: 'whatsapp_future', label: 'WhatsApp masivo' },
  { value: 'sms_future', label: 'SMS (próximamente)' },
];

export default function MarketingSimpleFlow() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(0);
  const [campaignId, setCampaignId] = useState(null);
  const [scheduleId, setScheduleId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Step 1
  const [name, setName] = useState('');
  const [objective, setObjective] = useState('promotional');
  const [channel, setChannel] = useState(() => searchParams.get('channel') || 'email_future');

  // Step 2
  const [audienceMode, setAudienceMode] = useState('existing');
  const [audiences, setAudiences] = useState([]);
  const [selectedAudienceId, setSelectedAudienceId] = useState('');
  const [csvRows, setCsvRows] = useState([]);
  const [csvErrors, setCsvErrors] = useState([]);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const fileRef = useRef(null);

  // Step 3
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [ctaText, setCtaText] = useState('');
  const [approvedTemplates, setApprovedTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Step 4
  const [scheduleMode, setScheduleMode] = useState('later');
  const [scheduledDate, setScheduledDate] = useState('');

  // Step 5
  const [securityResult, setSecurityResult] = useState(null);
  const [securityLoading, setSecurityLoading] = useState(false);

  // Step 6
  const [testRecipients, setTestRecipients] = useState([]);
  const [selectedTestRecipient, setSelectedTestRecipient] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);

  useEffect(() => {
    api.get('/tenant/audiences').then(r => setAudiences(r.data || [])).catch(() => {});
    api.get('/tenant/campaign-test-recipients').then(r => setTestRecipients(r.data?.items || [])).catch(() => {});
    api.get('/tenant/marketing/templates').then(r => {
      const approved = (r.data || []).filter(t => t.external_status === 'APPROVED' && t.status === 'active');
      setApprovedTemplates(approved);
    }).catch(() => {});
  }, []);

  // ── Step 1: Create campaign ──
  const handleStep1 = async () => {
    if (!name.trim()) { setError('Escribe un nombre para tu campaña'); return; }
    setLoading(true); setError('');
    try {
      const key = slugify(name) || `camp-${Date.now()}`;
      const { data } = await api.post('/tenant/campaigns', {
        key, name: name.trim(), objective: OBJECTIVES.find(o => o.value === objective)?.label || '',
        campaign_type: objective, planned_channels: [channel], tone: '', risk_level: 'low',
      });
      setCampaignId(data.id);
      toast.success('Campaña creada');
      setStep(1);
    } catch (e) { const msg = translateMarketingError(e.response?.data?.detail); setError(msg); toast.error(msg); }
    finally { setLoading(false); }
  };

  // ── Step 2: Link audience ──
  const handleLinkExisting = async () => {
    if (!selectedAudienceId || !campaignId) return;
    setLoading(true); setError('');
    try {
      await api.post(`/tenant/campaigns/${campaignId}/audiences/${selectedAudienceId}/link`);
      toast.success('Lista vinculada');
      setStep(2);
    } catch (e) { setError(translateMarketingError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const handleCsvFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
      if (lines.length < 2) { setCsvErrors(['El archivo está vacío o solo tiene encabezados.']); return; }
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      const required = ['nombre', 'email'];
      const missing = required.filter(r => !headers.includes(r));
      if (missing.length) { setCsvErrors([`Faltan columnas: ${missing.join(', ')}`]); return; }
      const rows = []; const errors = [];
      for (let i = 1; i < lines.length && i <= 51; i++) {
        const vals = lines[i].split(',').map(v => v.trim());
        const row = {};
        headers.forEach((h, idx) => { row[h] = vals[idx] || ''; });
        if (!row.nombre) { errors.push(`Fila ${i + 1}: falta nombre`); continue; }
        if (!row.email || !row.email.includes('@')) { errors.push(`Fila ${i + 1}: email inválido`); continue; }
        rows.push(row);
      }
      if (lines.length > 51) errors.push('Máximo 50 leads por archivo. Importa en lotes o usa modo avanzado.');
      setCsvRows(rows); setCsvErrors(errors);
    };
    reader.readAsText(file);
  };

  const handleCsvUpload = async () => {
    if (!csvRows.length || !campaignId) return;
    setCsvUploading(true); setError('');
    let created = 0; let failed = 0; const contactIds = [];
    try {
      for (const row of csvRows) {
        try {
          const { data } = await api.post('/tenant/contacts', {
            first_name: row.nombre || row.first_name || '', last_name: row.apellido || row.last_name || '',
            primary_email: row.email || '', primary_phone: row.telefono || row.phone || '',
            contact_type: 'lead', preferred_channel: '', tags: row.interes ? [row.interes] : [],
          });
          contactIds.push(data.id);
          created++;
        } catch { failed++; }
      }
      // Create audience + link
      const audKey = `leads-${slugify(name)}-${Date.now()}`;
      const { data: aud } = await api.post('/tenant/audiences', { key: audKey, name: `Leads: ${name}`, description: `Importados CSV (${created})` });
      for (const cid of contactIds) {
        try { await api.post(`/tenant/audiences/${aud.id}/members`, { contact_id: cid }); } catch { /* skip */ }
      }
      await api.post(`/tenant/campaigns/${campaignId}/audiences/${aud.id}/link`);
      setCsvResult({ created, failed, audienceId: aud.id });
      toast.success(`${created} leads importados y lista vinculada`);
      setStep(2);
    } catch (e) { setError(translateMarketingError(e.response?.data?.detail)); }
    finally { setCsvUploading(false); }
  };

  // ── Step 3: Add content ──
  const isWhatsApp = channel === 'whatsapp_future' || channel === 'whatsapp';
  const handleStep3 = async () => {
    if (isWhatsApp && !selectedTemplateId) { setError('Selecciona una plantilla aprobada para WhatsApp'); return; }
    if (!isWhatsApp && !body.trim()) { setError('Escribe el mensaje de tu campaña'); return; }
    setLoading(true); setError('');
    try {
      const contentBody = isWhatsApp
        ? { channel, content_type: 'message', subject: selectedTemplate?.name || '', body: selectedTemplate?.body_text || '',
            cta_text: '', variables: selectedTemplate?.variables || [],
            variable_examples: { template_id: selectedTemplateId, variable_map: {} } }
        : { channel, content_type: 'message', subject, body, cta_text: ctaText, variables: [], variable_examples: {} };
      await api.post(`/tenant/campaigns/${campaignId}/contents`, contentBody);
      toast.success('Mensaje guardado');
      setStep(3);
    } catch (e) { setError(translateMarketingError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  // ── Step 4: Schedule ──
  const handleStep4 = async () => {
    setLoading(true); setError('');
    try {
      // Submit for review + approve
      try { await api.post(`/tenant/campaigns/${campaignId}/submit-review`); } catch { /* may already be */ }
      try { await api.post(`/tenant/campaigns/${campaignId}/approve`); } catch { /* may already be */ }
      const body = { campaign_id: campaignId, planned_channel: channel, timezone: 'America/Mexico_City' };
      if (scheduleMode === 'schedule' && scheduledDate) body.planned_start_at = new Date(scheduledDate).toISOString();
      const { data } = await api.post('/tenant/campaign-schedules', body);
      setScheduleId(data.id);
      toast.success('Programación creada');
      setStep(4);
    } catch (e) { setError(translateMarketingError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  // ── Step 5: Security review ──
  const handleSecurityReview = async () => {
    if (!scheduleId) { setError('Falta programación'); return; }
    setSecurityLoading(true); setError(''); setSecurityResult(null);
    try {
      try { await api.post(`/tenant/campaign-schedules/${scheduleId}/audience-snapshot`); } catch { /* may exist */ }
      const { data: pf } = await api.post(`/tenant/campaign-schedules/${scheduleId}/preflight`);
      setSecurityResult(pf);
      if (pf.status === 'passed') {
        try { await api.post(`/tenant/campaign-schedules/${scheduleId}/mark-ready`); } catch { /* may be ready */ }
        toast.success('Revisión aprobada');
      }
    } catch (e) { setError(translateMarketingError(e.response?.data?.detail)); }
    finally { setSecurityLoading(false); }
  };

  // ── Step 6: Test send ──
  const handleTestSend = async () => {
    if (!selectedTestRecipient) { setError('Selecciona un contacto de prueba'); return; }
    if (confirmText !== 'CONFIRMO PRUEBA') { setError('Escribe "CONFIRMO PRUEBA" para confirmar'); return; }
    setTestLoading(true); setError(''); setTestResult(null);
    try {
      const ikey = `plan-wiz-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const { data: plan } = await api.post(`/tenant/campaign-schedules/${scheduleId}/delivery-plan`, {
        name: `Prueba: ${name}`, delivery_mode: 'test_send', idempotency_key: ikey,
      });
      await api.post(`/tenant/campaign-delivery/runs/${plan.id}/dry-run`);
      const tsKey = `ts-wiz-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const { data: ts } = await api.post(`/tenant/campaign-delivery/runs/${plan.id}/test-send`, {
        test_recipient_id: selectedTestRecipient, idempotency_key: tsKey, confirm_text: 'CONFIRMO PRUEBA',
      });
      setTestResult(ts);
      toast.success(ts.status === 'test_success' ? 'Prueba enviada correctamente' : 'Prueba preparada');
    } catch (e) { setError(translateMarketingError(e.response?.data?.detail)); }
    finally { setTestLoading(false); }
  };

  const canAdvance = () => {
    if (step === 0) return !!name.trim();
    if (step === 1) return true;
    if (step === 2) return !!body.trim();
    if (step === 3) return true;
    if (step === 4) return securityResult?.status === 'passed';
    return true;
  };

  return (
    <div className="space-y-5" data-testid="marketing-simple-flow">
      {/* Stepper */}
      <div className="flex items-center gap-1 overflow-x-auto pb-2" data-testid="wizard-stepper">
        {STEPS.map((s, i) => (
          <div key={s.key} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
            i === step ? 'bg-[#F72585] text-white' : i < step ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-400'
          }`}>
            {i < step ? <Check className="w-3 h-3" /> : <s.icon className="w-3 h-3" />}
            <span className="hidden sm:inline">{s.label}</span>
            <span className="sm:hidden">{i + 1}</span>
          </div>
        ))}
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded" data-testid="wizard-error">{error}<button className="ml-2 underline text-xs" onClick={() => setError('')}>cerrar</button></div>}

      {/* Step 1 */}
      {step === 0 && (
        <div className="bg-white border rounded-xl p-5 space-y-4" data-testid="wizard-step-1">
          <h3 className="text-sm font-bold">¿Qué quieres hacer?</h3>
          <div className="space-y-3">
            <div><label className="text-xs text-[#64748B]">Nombre de tu campaña</label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Ej: Promo Verano 2026" data-testid="wizard-campaign-name" /></div>
            <div><label className="text-xs text-[#64748B]">Objetivo</label>
              <select value={objective} onChange={e => setObjective(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="wizard-objective">
                {OBJECTIVES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select></div>
            <div><label className="text-xs text-[#64748B]">Canal principal</label>
              <select value={channel} onChange={e => setChannel(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="wizard-channel">
                {CHANNELS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select></div>
          </div>
          <div className="flex justify-end">
            <Button onClick={handleStep1} disabled={!name.trim() || loading} data-testid="wizard-next-1">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Siguiente <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 2 */}
      {step === 1 && (
        <div className="bg-white border rounded-xl p-5 space-y-4" data-testid="wizard-step-2">
          <h3 className="text-sm font-bold">¿A quién se la quieres mandar?</h3>
          <div className="flex gap-2">
            <button onClick={() => setAudienceMode('existing')} className={`px-3 py-1.5 text-xs rounded-full ${audienceMode === 'existing' ? 'bg-[#0F172A] text-white' : 'bg-gray-100 text-gray-600'}`}>Usar lista existente</button>
            <button onClick={() => setAudienceMode('csv')} className={`px-3 py-1.5 text-xs rounded-full ${audienceMode === 'csv' ? 'bg-[#0F172A] text-white' : 'bg-gray-100 text-gray-600'}`}>Subir CSV / base nueva</button>
          </div>
          {audienceMode === 'existing' && (
            <div className="space-y-2">
              <select value={selectedAudienceId} onChange={e => setSelectedAudienceId(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="wizard-audience-select">
                <option value="">Seleccionar lista de leads...</option>
                {audiences.filter(a => a.status === 'active').map(a => (
                  <option key={a.id} value={a.id}>{a.name} ({a.estimated_count} leads)</option>
                ))}
              </select>
              <Button onClick={handleLinkExisting} disabled={!selectedAudienceId || loading} size="sm" data-testid="wizard-link-audience">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Vincular lista
              </Button>
            </div>
          )}
          {audienceMode === 'csv' && (
            <div className="space-y-3">
              <p className="text-xs text-[#64748B]">Sube un archivo CSV con columnas: <strong>nombre, email</strong> (obligatorias), apellido, telefono, empresa, interes (opcionales).</p>
              <input type="file" accept=".csv" ref={fileRef} onChange={handleCsvFile} className="text-xs" data-testid="wizard-csv-input" />
              {csvErrors.length > 0 && <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-700 space-y-1">
                {csvErrors.map((e, i) => <p key={i}><AlertTriangle className="w-3 h-3 inline mr-1" />{e}</p>)}
              </div>}
              {csvRows.length > 0 && (
                <div className="text-xs text-emerald-700 bg-emerald-50 rounded p-2">
                  {csvRows.length} leads listos para importar
                </div>
              )}
              <Button onClick={handleCsvUpload} disabled={!csvRows.length || csvUploading} size="sm" data-testid="wizard-csv-upload">
                {csvUploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />} Importar leads y crear lista
              </Button>
              {csvResult && <p className="text-xs text-emerald-600">{csvResult.created} leads creados{csvResult.failed > 0 ? `, ${csvResult.failed} fallidos` : ''}</p>}
            </div>
          )}
          <div className="flex justify-between">
            <Button variant="outline" size="sm" onClick={() => setStep(0)}><ChevronLeft className="w-4 h-4 mr-1" /> Atrás</Button>
            <Button onClick={() => setStep(2)} disabled={!canAdvance()} size="sm" data-testid="wizard-next-2">Siguiente <ChevronRight className="w-4 h-4 ml-1" /></Button>
          </div>
        </div>
      )}

      {/* Step 3 */}
      {step === 2 && (
        <div className="bg-white border rounded-xl p-5 space-y-4" data-testid="wizard-step-3">
          <h3 className="text-sm font-bold">¿Qué mensaje quieres enviar?</h3>
          {isWhatsApp ? (
            <div className="space-y-3">
              {approvedTemplates.length === 0 ? (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800 font-medium">Aún no tienes plantillas aprobadas por Meta</p>
                  <p className="text-xs text-amber-700 mt-1">Para enviar campañas por WhatsApp, Meta debe aprobar tus plantillas primero.</p>
                  <Button size="sm" variant="outline" className="mt-2" onClick={() => window.location.href='/tenant/marketing/templates'}>Ir a Plantillas</Button>
                </div>
              ) : (
                <>
                  <div>
                    <label className="text-xs text-[#64748B] font-semibold">Plantilla aprobada</label>
                    <select value={selectedTemplateId} onChange={e => {
                      setSelectedTemplateId(e.target.value);
                      setSelectedTemplate(approvedTemplates.find(t => t.id === e.target.value) || null);
                    }} className="w-full mt-1 px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg" data-testid="campaign-template-select">
                      <option value="">Seleccionar plantilla...</option>
                      {approvedTemplates.map(t => <option key={t.id} value={t.id}>{t.name} ({t.category_label})</option>)}
                    </select>
                  </div>
                  {selectedTemplate && (
                    <div className="bg-[#E5DDD5] rounded-lg p-4" data-testid="template-preview">
                      <div className="bg-white rounded-lg p-3 max-w-[280px] shadow-sm">
                        {selectedTemplate.header_text && <p className="text-sm font-semibold mb-1">{selectedTemplate.header_text}</p>}
                        <p className="text-sm whitespace-pre-wrap">{selectedTemplate.body_text}</p>
                        {selectedTemplate.footer_text && <p className="text-[10px] text-[#94A3B8] mt-1">{selectedTemplate.footer_text}</p>}
                      </div>
                    </div>
                  )}
                  {selectedTemplate?.variables?.length > 0 && (
                    <div className="space-y-1">
                      <label className="text-xs text-[#64748B] font-semibold">Variables</label>
                      {selectedTemplate.variables.map(v => (
                        <div key={v.key} className="flex items-center gap-2 text-xs">
                          <span className="px-2 py-0.5 bg-violet-50 text-violet-700 rounded">{`{{${v.key}}}`}</span>
                          <span className="text-[#64748B]">→ dato del contacto</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div><label className="text-xs text-[#64748B]">Título o asunto</label>
                <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Ej: Promoción especial para ti" data-testid="wizard-subject" /></div>
              <div><label className="text-xs text-[#64748B]">Mensaje</label>
                <Textarea value={body} onChange={e => setBody(e.target.value)} className="h-28"
                  placeholder="Hola {{nombre}}, tenemos una oferta especial..." data-testid="wizard-body" /></div>
              <div><label className="text-xs text-[#64748B]">Botón o texto de acción (opcional)</label>
                <Input value={ctaText} onChange={e => setCtaText(e.target.value)} placeholder="Ej: Agendar cita" data-testid="wizard-cta" /></div>
              <div className="flex flex-wrap gap-1">
                {['{{nombre}}', '{{empresa}}', '{{interes}}'].map(v => (
                  <button key={v} onClick={() => setBody(b => b + ' ' + v)} className="text-[10px] px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full hover:bg-blue-100">{v}</button>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-between">
            <Button variant="outline" size="sm" onClick={() => setStep(1)}><ChevronLeft className="w-4 h-4 mr-1" /> Atrás</Button>
            <Button onClick={handleStep3} disabled={(isWhatsApp ? !selectedTemplateId : !body.trim()) || loading} size="sm" data-testid="wizard-next-3">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Siguiente <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 4 */}
      {step === 3 && (
        <div className="bg-white border rounded-xl p-5 space-y-4" data-testid="wizard-step-4">
          <h3 className="text-sm font-bold">¿Cuándo quieres hacerlo?</h3>
          <div className="space-y-2">
            {[{ value: 'later', label: 'Ahora no, solo preparar' }, { value: 'schedule', label: 'Programar fecha' }, { value: 'test_only', label: 'Solo enviar prueba' }].map(opt => (
              <label key={opt.value} className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-[#F8FAFC]">
                <input type="radio" name="schedule_mode" value={opt.value} checked={scheduleMode === opt.value} onChange={() => setScheduleMode(opt.value)} />
                <span className="text-sm">{opt.label}</span>
              </label>
            ))}
            {scheduleMode === 'schedule' && (
              <div className="pl-8"><label className="text-xs text-[#64748B]">Fecha y hora</label>
                <input type="datetime-local" value={scheduledDate} onChange={e => setScheduledDate(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="wizard-schedule-date" /></div>
            )}
          </div>
          <div className="flex justify-between">
            <Button variant="outline" size="sm" onClick={() => setStep(2)}><ChevronLeft className="w-4 h-4 mr-1" /> Atrás</Button>
            <Button onClick={handleStep4} disabled={loading} size="sm" data-testid="wizard-next-4">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Siguiente <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 5 */}
      {step === 4 && (
        <div className="bg-white border rounded-xl p-5 space-y-4" data-testid="wizard-step-5">
          <h3 className="text-sm font-bold">Revisión de seguridad</h3>
          <p className="text-xs text-[#64748B]">Verificamos que tu campaña, lista de leads y permisos estén correctos antes de enviar una prueba.</p>
          {!securityResult && (
            <Button onClick={handleSecurityReview} disabled={securityLoading} data-testid="wizard-security-review-btn">
              {securityLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Shield className="w-4 h-4 mr-1" />} Revisar seguridad
            </Button>
          )}
          {securityResult && (
            <div className="space-y-2">
              <div className={`text-sm font-medium ${securityResult.status === 'passed' ? 'text-emerald-600' : securityResult.status === 'failed' ? 'text-red-600' : 'text-amber-600'}`}>
                {securityResult.status === 'passed' ? 'Revisión aprobada' : securityResult.status === 'failed' ? 'Revisión con errores' : 'Revisión con advertencias'}
              </div>
              {(securityResult.checks || []).map((c, i) => (
                <div key={i} className="flex items-center gap-1.5 text-xs">
                  {c.passed ? <Check className="w-3 h-3 text-emerald-500" /> : <AlertTriangle className="w-3 h-3 text-red-500" />}
                  <span>{c.name}</span>
                </div>
              ))}
              {securityResult.blocking_errors?.length > 0 && (
                <div className="text-xs text-red-600 bg-red-50 p-2 rounded space-y-1">
                  {securityResult.blocking_errors.map((e, i) => <p key={i}>{translateMarketingError(e)}</p>)}
                </div>
              )}
            </div>
          )}
          <div className="flex justify-between">
            <Button variant="outline" size="sm" onClick={() => setStep(3)}><ChevronLeft className="w-4 h-4 mr-1" /> Atrás</Button>
            <Button onClick={() => setStep(5)} disabled={securityResult?.status !== 'passed'} size="sm" data-testid="wizard-next-5">
              Siguiente <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 6 */}
      {step === 5 && (
        <div className="bg-white border rounded-xl p-5 space-y-4" data-testid="wizard-step-6">
          <h3 className="text-sm font-bold">Enviar prueba</h3>
          <p className="text-xs text-[#64748B]">Esto enviará un mensaje solo al contacto de prueba. No se enviará a toda la lista.</p>
          <div className="space-y-3">
            <div><label className="text-xs text-[#64748B]">Contacto de prueba</label>
              <select value={selectedTestRecipient} onChange={e => setSelectedTestRecipient(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="wizard-test-recipient">
                <option value="">Seleccionar...</option>
                {testRecipients.filter(t => t.status === 'active').map(t => (
                  <option key={t.id} value={t.id}>{t.label || t.channel} ({t.contact_id.slice(0, 8)})</option>
                ))}
              </select>
              {testRecipients.length === 0 && <p className="text-[10px] text-amber-600 mt-1">No tienes contactos de prueba. <button className="underline" onClick={() => navigate('/tenant/marketing/delivery')}>Crear uno en Pruebas</button></p>}
            </div>
            <div><label className="text-xs text-[#64748B]">Escribe &quot;CONFIRMO PRUEBA&quot; para confirmar:</label>
              <Input value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="CONFIRMO PRUEBA" data-testid="wizard-confirm-text" /></div>
          </div>
          <SensitiveActionHelp action={sensitiveActions.delivery_test_send} onConfirm={handleTestSend} disabled={confirmText !== 'CONFIRMO PRUEBA' || !selectedTestRecipient || testLoading}>
            <Button disabled={confirmText !== 'CONFIRMO PRUEBA' || !selectedTestRecipient || testLoading} data-testid="wizard-test-send-btn">
              {testLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Zap className="w-4 h-4 mr-1" />} Enviar prueba
            </Button>
          </SensitiveActionHelp>
          {testResult && (
            <div className={`text-sm font-medium p-3 rounded ${testResult.status === 'test_success' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`} data-testid="wizard-test-result">
              {testResult.status === 'test_success' ? 'Prueba enviada correctamente' : 'Prueba preparada (simulación)'}
            </div>
          )}
          <div className="flex justify-between">
            <Button variant="outline" size="sm" onClick={() => setStep(4)}><ChevronLeft className="w-4 h-4 mr-1" /> Atrás</Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/tenant/marketing')} data-testid="wizard-finish">
              Finalizar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
